import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Storefront, Eye } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { initials, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Pager } from "@/components/Pager";

const statusColor = { pending: "text-amber-700 bg-amber-500/10", active: "text-emerald-700 bg-emerald-600/10", suspended: "text-destructive bg-destructive/10", rejected: "text-muted-foreground bg-black/5" };

export default function AdminBusinesses() {
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState("all");
  const PAGE = 10;

  const load = () => api.get("/admin/businesses", { params: { ...(filter !== "all" ? { status: filter } : {}), limit: PAGE, offset: page * PAGE } }).then((r) => { setList(r.data.items); setTotal(r.data.total); }).catch(() => setList([]));
  useEffect(() => { load(); }, [filter, page]); // eslint-disable-line
  useEffect(() => { setPage(0); }, [filter]);

  const act = async (id, action) => {
    const verb = { approve: "approved", reject: "rejected", suspend: "suspended", reactivate: "reactivated" }[action] || `${action}d`;
    try { await api.post(`/admin/businesses/${id}/${action}`); toast.success(`Business ${verb}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <h1 className="font-display font-bold text-3xl flex items-center gap-2"><Storefront size={26} className="text-primary" /> Businesses</h1>
      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-muted/50 rounded-full">
          {["all", "pending", "active", "suspended", "rejected"].map((s) => (
            <TabsTrigger key={s} value={s} data-testid={`filter-${s}`} className="rounded-full capitalize data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">{s}</TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {!list && <p className="text-muted-foreground">Loading…</p>}
        {list && list.length === 0 && <p className="text-muted-foreground text-sm">No businesses.</p>}
        {list && list.map((b) => (
          <div key={b.id} data-testid="admin-business-row" className="card-surface p-5 flex items-center gap-4 flex-wrap">
            <div className="h-11 w-11 rounded-xl bg-primary/15 flex items-center justify-center font-display font-bold text-primary">{initials(b.name)}</div>
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold">{b.name}</p>
              <p className="text-xs text-muted-foreground">{b.category} · {b.city}, {b.country} · {b.branches} branch · {b.staff} staff · joined {fmtDate(b.created_at)}</p>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full capitalize ${statusColor[b.status]}`}>{b.status}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" asChild data-testid="view-business-btn" className="rounded-full border-black/10">
                <Link to={`/admin/businesses/${b.id}`}><Eye size={15} className="mr-1" /> View</Link>
              </Button>
              {b.status === "pending" && <>
                <Button size="sm" data-testid="approve-btn" onClick={() => act(b.id, "approve")} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Approve</Button>
                <Button size="sm" variant="outline" data-testid="reject-btn" onClick={() => act(b.id, "reject")} className="rounded-full border-black/10">Reject</Button>
              </>}
              {b.status === "active" && <Button size="sm" variant="outline" data-testid="suspend-btn" onClick={() => act(b.id, "suspend")} className="rounded-full border-black/10 text-destructive">Suspend</Button>}
              {(b.status === "suspended" || b.status === "rejected") && <Button size="sm" data-testid="reactivate-btn" onClick={() => act(b.id, "reactivate")} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">Reactivate</Button>}
            </div>
          </div>
        ))}
      </div>
      <Pager page={page} total={total} pageSize={PAGE} onPage={setPage} />
    </div>
  );
}
